// ExportRedevances.jsx
// Export PDF et Excel pour les redevances
// Niveaux : Tout | Retard 1-2 mois | Alerte 3 mois | Critique 4+ mois

import React, { useState } from 'react';
import { Download, FileSpreadsheet, FileText, ChevronDown, X, Loader } from 'lucide-react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { api } from '../services/api';

const RISK_LEVELS = [
  { key:'all',      label:'Tout',     sublabel:'Tous les clients impayes', color:'#2563eb', bgClass:'bg-blue-50 border-blue-200 text-blue-700',   dotClass:'bg-blue-500',   apiParam:undefined  },
  { key:'warning',  label:'Retard',   sublabel:'1 - 2 mois',               color:'#d97706', bgClass:'bg-yellow-50 border-yellow-200 text-yellow-700', dotClass:'bg-yellow-500', apiParam:'warning'  },
  { key:'alert',    label:'Alerte',   sublabel:'3 mois',                   color:'#ea580c', bgClass:'bg-orange-50 border-orange-200 text-orange-700', dotClass:'bg-orange-500', apiParam:'alert'    },
  { key:'critical', label:'Critique', sublabel:'4 mois +',                 color:'#dc2626', bgClass:'bg-red-50 border-red-200 text-red-700',         dotClass:'bg-red-500',    apiParam:'critical' },
];

const fmtCFA = (n) => {
  const formatted = new Intl.NumberFormat('fr-FR').format(n ?? 0);
  return formatted.replace(/[\u202f\u00a0]/g, ' ') + ' FCFA';
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : 'N/A';
const MOIS = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];
const getMonthName = (m) => MOIS[(m ?? 1) - 1] || '';
const getRiskLabel = (months) => {
  if (!months || months === 0) return 'A jour';
  if (months <= 2) return 'Retard';
  if (months === 3) return 'Alerte';
  return 'Critique';
};

async function fetchAllData(riskParam) {
  const PAGE = 100;
  let skip = 0, all = [], total = null;
  do {
    const params = { skip, limit: PAGE, status: 'unpaid' };
    if (riskParam) params.risk_level = riskParam;
    const res = await api.get('/monthly-fees/consolidated', { params });
    const body = res.data;
    if (total === null) total = body.total ?? 0;
    const rows = body.data || [];
    all = all.concat(rows);
    skip += PAGE;
    if (rows.length < PAGE) break;
  } while (all.length < total);
  return all;
}

async function exportExcel(level, setLoading) {
  setLoading(`excel_${level.key}`);
  try {
    const data = await fetchAllData(level.apiParam);
    if (!data.length) { toast.warning('Aucune donnee pour ce filtre'); return; }

    const rows = data.map((item, idx) => ({
      'N°':                   idx + 1,
      'Compteur':             item.meter_id         || 'N/A',
      'Client':               item.customer_name    || 'N/A',
      'Telephone':            item.customer_phone   || 'N/A',
      'Adresse':              item.customer_address || 'N/A',
      'Departement':          item.departement             || 'N/A',
      'Mois impayes':         item.months_owed      ?? 0,
      'Niveau risque':        getRiskLabel(item.months_owed),
      'Montant du (FCFA)':    Number(item.remaining_amount ?? 0),
      'Total facture (FCFA)': Number(item.total_amount     ?? 0),
      'Deja paye (FCFA)':     Number(item.total_paid       ?? 0),
      'Periode ancienne':     item.oldest_month ? `${getMonthName(item.oldest_month)} ${item.oldest_year}` : 'N/A',
      'Echeance':             fmtDate(item.due_date),
      'Statut compteur':      item.meter_status === 'suspended' ? 'Bloque' : 'Actif',
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{wch:5},{wch:18},{wch:30},{wch:18},{wch:35},{wch:14},{wch:14},{wch:16},{wch:20},{wch:22},{wch:20},{wch:24},{wch:14},{wch:16}];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Redevances - ${level.label}`);

    const totalDu = data.reduce((s,r) => s + Number(r.remaining_amount ?? 0), 0);
    const summary = [
      { 'Indicateur': 'Filtre',            'Valeur': `${level.label} - ${level.sublabel}` },
      { 'Indicateur': 'Date export',        'Valeur': new Date().toLocaleString('fr-FR') },
      { 'Indicateur': 'Nombre clients',     'Valeur': data.length },
      { 'Indicateur': 'Total montant du',   'Valeur': fmtCFA(totalDu) },
      { 'Indicateur': 'Retard (1-2 mois)',  'Valeur': data.filter(r=>(r.months_owed??0)>=1&&(r.months_owed??0)<=2).length },
      { 'Indicateur': 'Alerte (3 mois)',    'Valeur': data.filter(r=>(r.months_owed??0)===3).length },
      { 'Indicateur': 'Critique (4+ mois)', 'Valeur': data.filter(r=>(r.months_owed??0)>=4).length },
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summary);
    wsSummary['!cols'] = [{wch:28},{wch:35}];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Resume');

    XLSX.writeFile(wb, `redevances_${level.key}_${new Date().toISOString().slice(0,10)}.xlsx`);
    toast.success(`Excel exporte : ${data.length} client(s)`);
  } catch(e) {
    console.error(e);
    toast.error('Erreur Excel : ' + e.message);
  } finally {
    setLoading(null);
  }
}

async function exportPDF(level, setLoading) {
  setLoading(`pdf_${level.key}`);
  try {
    const data = await fetchAllData(level.apiParam);
    if (!data.length) { toast.warning('Aucune donnee pour ce filtre'); return; }

    const doc = new jsPDF({ orientation:'landscape', unit:'mm', format:'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const today = new Date().toLocaleDateString('fr-FR', {day:'2-digit',month:'long',year:'numeric'});

    doc.setFillColor(15,23,42); doc.rect(0,0,pageW,22,'F');
    doc.setTextColor(255,255,255); doc.setFontSize(16); doc.setFont('helvetica','bold');
    doc.text('MAD POWER', 14, 9);
    doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(148,163,184);
    doc.text('Rapport des Redevances Impayes', 14, 16);
    doc.setFontSize(10); doc.setFont('helvetica','bold'); doc.setTextColor(255,255,255);
    doc.text(`${level.label} - ${level.sublabel}`, pageW-14, 9, {align:'right'});
    doc.setFontSize(8); doc.setFont('helvetica','normal'); doc.setTextColor(148,163,184);
    doc.text(`Genere le ${today}`, pageW-14, 16, {align:'right'});

    const totalDu    = data.reduce((s,r) => s + Number(r.remaining_amount??0), 0);
    const nbRetard   = data.filter(r=>(r.months_owed??0)>=1&&(r.months_owed??0)<=2).length;
    const nbAlerte   = data.filter(r=>(r.months_owed??0)===3).length;
    const nbCritique = data.filter(r=>(r.months_owed??0)>=4).length;

    const stats = [
      {label:'Clients',        value:data.length.toString(), color:[59,130,246]},
      {label:'Total du',       value:fmtCFA(totalDu),         color:[220,38,38]},
      {label:'Retard (1-2m)',  value:nbRetard.toString(),      color:[217,119,6]},
      {label:'Alerte (3m)',    value:nbAlerte.toString(),      color:[234,88,12]},
      {label:'Critique (4m+)', value:nbCritique.toString(),    color:[185,28,28]},
    ];
    const boxW = (pageW - 28 - 12) / 5;
    stats.forEach((stat, i) => {
      const x = 14 + i * (boxW + 3), y = 26;
      doc.setFillColor(248,250,252); doc.setDrawColor(226,232,240);
      doc.roundedRect(x, y, boxW, 14, 2, 2, 'FD');
      doc.setFillColor(...stat.color); doc.rect(x, y, 2.5, 14, 'F');
      doc.setTextColor(...stat.color); doc.setFontSize(12); doc.setFont('helvetica','bold');
      doc.text(stat.value, x+5, y+7);
      doc.setTextColor(100,116,139); doc.setFontSize(6.5); doc.setFont('helvetica','normal');
      doc.text(stat.label, x+5, y+12);
    });

    const tableRows = data.map((item, idx) => {
      const m = item.months_owed ?? 0;
      return [idx+1, item.meter_id||'N/A', item.customer_name||'N/A', item.customer_phone||'N/A',
              item.customer_address||'N/A', item.departement||'N/A', m, getRiskLabel(m),
              fmtCFA(item.remaining_amount),
              item.oldest_month ? `${getMonthName(item.oldest_month)} ${item.oldest_year}` : 'N/A',
              fmtDate(item.due_date),
              item.meter_status==='suspended' ? 'Bloque' : 'Actif'];
    });

    autoTable(doc, {
      startY: 44,
      head: [['N°','Compteur','Client','Telephone','Adresse','Departement','Mois','Risque','Montant du','Periode','Echeance','Statut']],
      body: tableRows,
      styles: {fontSize:7.2, cellPadding:{top:2.5,right:2.5,bottom:2.5,left:2.5}, font:'helvetica', textColor:[30,41,59], lineColor:[226,232,240], lineWidth:0.2},
      headStyles: {fillColor:[15,23,42], textColor:[255,255,255], fontStyle:'bold', fontSize:7.2, halign:'center'},
      columnStyles: {
            0:  { halign:'center', cellWidth: 7 },
            1:  { cellWidth: 26, font: 'courier' },
            2:  { cellWidth: 34 },
            3:  { cellWidth: 24 },
            4:  { cellWidth: 36 },
            5:  { cellWidth: 14 },
            6:  { halign:'center', cellWidth: 11 },
            7:  { halign:'center', cellWidth: 18 },
            8:  { halign:'right',  cellWidth: 32, fontStyle:'bold' },  // ← était 26
            9:  { cellWidth: 22 },   // légèrement réduit pour compenser
            10: { cellWidth: 20 },
            11: { halign:'center', cellWidth: 14 },
        },
      alternateRowStyles: {fillColor:[248,250,252]},
      didDrawCell: (h) => {
        if (h.section==='body' && h.column.index===7) {
          const fills = {'Critique':[254,226,226],'Alerte':[255,237,213],'Retard':[254,249,195],'A jour':[220,252,231]};
          const fill = fills[h.cell.raw];
          if (fill) {
            doc.setFillColor(...fill);
            doc.rect(h.cell.x, h.cell.y, h.cell.width, h.cell.height, 'F');
            doc.setFontSize(7.2); doc.setTextColor(30,41,59);
            doc.text(h.cell.raw, h.cell.x+h.cell.width/2, h.cell.y+h.cell.height/2+1, {align:'center'});
          }
        }
      },
      didDrawPage: () => {
        const pg = doc.internal.getCurrentPageInfo().pageNumber;
        const tot = doc.internal.getNumberOfPages();
        const hh  = doc.internal.pageSize.getHeight();
        doc.setFontSize(7); doc.setTextColor(148,163,184);
        doc.text(`MAD POWER - Rapport Redevances - ${today}`, 14, hh-5);
        doc.text(`Page ${pg} / ${tot}`, pageW-14, hh-5, {align:'right'});
      },
    });

    const finalY = doc.lastAutoTable.finalY + 4;
    if (finalY < doc.internal.pageSize.getHeight()-15) {
      doc.setFillColor(15,23,42); doc.rect(14, finalY, pageW-28, 8, 'F');
      doc.setTextColor(255,255,255); doc.setFontSize(8); doc.setFont('helvetica','bold');
      doc.text(`TOTAL - ${data.length} client(s)`, 18, finalY+5);
      doc.text(fmtCFA(totalDu), pageW-18, finalY+5, {align:'right'});
    }

    doc.save(`redevances_${level.key}_${new Date().toISOString().slice(0,10)}.pdf`);
    toast.success(`PDF exporte : ${data.length} client(s)`);
  } catch(e) {
    console.error(e);
    toast.error('Erreur PDF : ' + e.message);
  } finally {
    setLoading(null);
  }
}

const ExportRedevances = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(null);
  const anyLoading = loading !== null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)} disabled={anyLoading}
        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white rounded-lg font-medium transition-all shadow-sm"
      >
        {anyLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        <span>{anyLoading ? 'Export en cours...' : 'Exporter'}</span>
        {!anyLoading && <ChevronDown className={`w-4 h-4 transition-transform ${open?'rotate-180':''}`} />}
      </button>

      {open && !anyLoading && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 z-50 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div>
                <p className="text-sm font-semibold text-gray-900">Exporter les redevances</p>
                <p className="text-xs text-gray-500 mt-0.5">Choisissez un niveau puis le format</p>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-gray-200 rounded-lg">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="p-3 space-y-2">
              {RISK_LEVELS.map(level => (
                <div key={level.key} className={`border rounded-xl p-3 ${level.bgClass}`}>
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${level.dotClass}`} />
                    <span className="text-sm font-bold">{level.label}</span>
                    <span className="text-xs opacity-70">— {level.sublabel}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setOpen(false); exportExcel(level, setLoading); }} disabled={anyLoading}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white hover:bg-green-50 border border-green-300 hover:border-green-500 text-green-700 rounded-lg text-xs font-semibold transition-all disabled:opacity-50">
                      <FileSpreadsheet className="w-3.5 h-3.5" /> Excel (.xlsx)
                    </button>
                    <button onClick={() => { setOpen(false); exportPDF(level, setLoading); }} disabled={anyLoading}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white hover:bg-red-50 border border-red-300 hover:border-red-500 text-red-700 rounded-lg text-xs font-semibold transition-all disabled:opacity-50">
                      <FileText className="w-3.5 h-3.5" /> PDF (.pdf)
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-400 text-center">
                Inclut : compteur • client • telephone • adresse • departement • mois impayes • montant du
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExportRedevances;